/**
 * Pure decision function for the tenant subscription lifecycle sweeper
 * (`lifecycleDaily` in apps/functions). Extracted so the transition table is a
 * single, unit-testable source of truth (repo rule #9) rather than inline
 * `if (status === …)` logic — and so the "all invoices paid → reactivate" path
 * that the manual mark-paid + Billplz flows rely on is provably correct.
 *
 * Side-effect-free: callers gather the inputs (tenant status, trial/suspend
 * timestamps, invoice statuses) and apply whatever transition this returns.
 */

/** Matches `TenantStatusSchema` in ../schemas/tenant.ts (kept literal here so
 * this module stays pure/dependency-free). */
type TenantStatus = 'trial' | 'active' | 'past_due' | 'suspended' | 'archived';

export interface LifecycleInvoice {
  /** Invoice status; only `open`/`past_due` count as unpaid. */
  readonly status: string;
  /** Due date in epoch ms, or null if unset. */
  readonly dueMs: number | null;
}

export interface LifecycleConfig {
  /** Days past dueDate before `active → past_due` (default 0 — due means due). */
  readonly pastDueGraceDays: number;
  /** Days overdue before `past_due → suspended` (default 30). */
  readonly suspendAfterDays: number;
  /** Days suspended before `suspended → archived` (default 90). */
  readonly suspendedRetentionDays: number;
}

export const DEFAULT_LIFECYCLE_CONFIG: LifecycleConfig = {
  pastDueGraceDays: 0,
  suspendAfterDays: 30,
  suspendedRetentionDays: 90,
};

export interface LifecycleInputs {
  readonly fromStatus: TenantStatus;
  readonly nowMs: number;
  readonly trialEndsMs: number | null;
  readonly suspendedMs: number | null;
  readonly invoices: readonly LifecycleInvoice[];
  readonly config?: LifecycleConfig;
}

export interface LifecycleDecision {
  readonly toStatus: TenantStatus | 'no_change';
  readonly reason: string;
  /** Days the oldest unpaid invoice is overdue (0 when none) — for logging. */
  readonly overdueDays: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** True when any invoice is in an unpaid state (`open`/`past_due`). */
export function anyInvoiceUnpaid(invoices: readonly LifecycleInvoice[]): boolean {
  return invoices.some((i) => i.status === 'open' || i.status === 'past_due');
}

export function decideTenantLifecycle(input: LifecycleInputs): LifecycleDecision {
  const cfg = input.config ?? DEFAULT_LIFECYCLE_CONFIG;
  const { fromStatus, nowMs } = input;

  // Terminal: archived tenants never transition.
  if (fromStatus === 'archived') {
    return { toStatus: 'no_change', reason: 'archived (terminal)', overdueDays: 0 };
  }

  // trial → active once the trial window has passed.
  if (fromStatus === 'trial') {
    const trialEndsMs = input.trialEndsMs ?? 0;
    if (trialEndsMs > 0 && trialEndsMs <= nowMs) {
      return { toStatus: 'active', reason: 'trial_ended', overdueDays: 0 };
    }
    return { toStatus: 'no_change', reason: 'still in trial', overdueDays: 0 };
  }

  // Outstanding-invoice state for the remaining transitions.
  let oldestUnpaidDueMs: number | null = null;
  let unpaid = false;
  for (const inv of input.invoices) {
    if (inv.status === 'open' || inv.status === 'past_due') {
      unpaid = true;
      const dueMs = inv.dueMs ?? 0;
      if (dueMs > 0 && (oldestUnpaidDueMs === null || dueMs < oldestUnpaidDueMs)) {
        oldestUnpaidDueMs = dueMs;
      }
    }
  }
  const overdueDays = oldestUnpaidDueMs ? Math.floor((nowMs - oldestUnpaidDueMs) / DAY_MS) : 0;

  // active → past_due: any invoice past the grace period.
  if (
    fromStatus === 'active' &&
    unpaid &&
    overdueDays >= cfg.pastDueGraceDays &&
    oldestUnpaidDueMs !== null &&
    oldestUnpaidDueMs <= nowMs
  ) {
    return { toStatus: 'past_due', reason: `invoice_overdue_${overdueDays}d`, overdueDays };
  }

  // past_due → suspended: overdue past the suspension window.
  if (fromStatus === 'past_due' && overdueDays >= cfg.suspendAfterDays) {
    return { toStatus: 'suspended', reason: `overdue_${overdueDays}d`, overdueDays };
  }

  // past_due → active AND suspended → active: all invoices paid (caught up).
  if ((fromStatus === 'past_due' || fromStatus === 'suspended') && !unpaid) {
    return { toStatus: 'active', reason: 'invoices_settled', overdueDays };
  }

  // suspended → archived: past the retention window without paying.
  if (fromStatus === 'suspended') {
    const suspendedMs = input.suspendedMs ?? 0;
    if (suspendedMs > 0 && nowMs - suspendedMs >= cfg.suspendedRetentionDays * DAY_MS) {
      return { toStatus: 'archived', reason: 'retention_window_elapsed', overdueDays };
    }
  }

  return { toStatus: 'no_change', reason: 'no transition needed', overdueDays };
}
